import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Star, TrendingUp, AlertTriangle, Target, Clock, Check, Zap } from "lucide-react";
import { servicesContent, servicesCategories } from "@/data/servicesContent";
import { buildCategorySalesSections } from "@/data/serviceSalesCopy";

export default function ServiceCategory({ categoryKey }) {
  const cat = servicesCategories[categoryKey];
  if (!cat) return null;
  const tiers = cat.slugs.map((slug) => servicesContent[slug]).filter(Boolean);
  const sales = buildCategorySalesSections(categoryKey, cat.label, tiers);

  return (
    <div className="bg-[#0B0C10]" data-testid={`services-category-${categoryKey}`}>
      {/* Hero */}
      <section className="relative border-b border-[#1F2833] py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 te-grid-bg-dense opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <nav className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]/70 mb-6">
            <Link to="/" className="hover:text-[#FF3355]">home</Link>
            <span className="mx-2">/</span>
            <Link to="/services" className="hover:text-[#FF3355]">services</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF3355]">{cat.label}</span>
          </nav>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">{cat.kicker}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-5xl">
            {cat.label}<span className="text-[#FF3355]">.</span>
          </h1>
          <p className="mt-6 font-display text-xl text-[#FF3355]">{cat.tagline}</p>
          <p className="mt-6 text-[#C5C6C7] max-w-3xl leading-relaxed">{cat.intro}</p>
        </div>
      </section>

      {/* ============ VIDEO SHOWCASE (only for AI Video category) ============ */}
      {categoryKey === "video" && (
        <section className="relative py-14 lg:py-20 border-b border-[#1F2833] overflow-hidden" data-testid="video-showcase">
          <div className="absolute inset-0 te-grid-bg opacity-20" aria-hidden />
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#FF3355]/10 blur-[120px]" aria-hidden />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid lg:grid-cols-[1fr_1.15fr] gap-10 lg:gap-14 items-center">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4 inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 te-pulse-dot" />
                  // FEATURED.CLIENT.SHOWCASE
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.02]">
                  DW Investment Group — signature <span className="text-[#FF3355]">brand ad</span>.
                </h2>
                <p className="mt-5 text-[#C5C6C7] leading-relaxed max-w-xl">
                  Cinematic 60-second investor-facing ad blending real footage with AI-augmented b-roll, motion graphics, and a scripted VO track. Engineered for LinkedIn feed, YouTube pre-roll, and investor deck loops. Shipped in <span className="text-emerald-400 font-semibold">10 days</span>.
                </p>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  <div className="border border-[#1F2833] bg-[#0d1117] px-3 py-3">
                    <div className="font-display text-2xl font-bold text-[#FF3355]">4.1x</div>
                    <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">meeting-book uplift · Q2</div>
                  </div>
                  <div className="border border-[#1F2833] bg-[#0d1117] px-3 py-3">
                    <div className="font-display text-2xl font-bold text-emerald-400">10 days</div>
                    <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">script → final render</div>
                  </div>
                  <div className="border border-[#1F2833] bg-[#0d1117] px-3 py-3">
                    <div className="font-display text-2xl font-bold text-[#F5F5F7]">60s</div>
                    <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">cinematic · 16:9 · 4K</div>
                  </div>
                </div>
                <div className="mt-6 flex flex-wrap gap-1.5">
                  {["Veo 3", "Kling", "Motion Graphics", "Investor Brand", "Scripted VO"].map((t) => (
                    <span key={t} className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#C5C6C7] border border-[#1F2833] px-2 py-0.5">{t}</span>
                  ))}
                </div>
                <Link to="/work" data-testid="video-showcase-cta" className="mt-7 inline-flex items-center gap-2 border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10 hover:border-emerald-400 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] font-bold transition-all">
                  See the full video catalog <ArrowUpRight size={13} />
                </Link>
              </div>

              <div className="relative">
                <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-[#FF3355]/25 via-[#7c3aed]/20 to-emerald-500/15 blur-2xl opacity-70" aria-hidden />
                <div className="relative rounded-2xl overflow-hidden border border-[#1F2833] bg-[#0B0C10] shadow-[0_20px_80px_-20px_rgba(0,0,0,0.7)]" data-testid="video-showcase-player">
                  <video
                    src="https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/eedhr5il_mp4_gwr_video_mvp.mp4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    className="w-full aspect-video object-cover bg-black"
                  />
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#0B0C10]/85 backdrop-blur-sm border border-emerald-400/40 text-emerald-400 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.22em] font-bold">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 te-pulse-dot" /> DW Investment Group
                  </div>
                  <div className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.22em] text-[#C5C6C7]/80 bg-[#0B0C10]/85 backdrop-blur-sm border border-[#1F2833] px-2 py-1">
                    2026 · Signature Ad
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Service cards */}
      <section className="py-16 lg:py-20 border-b border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-8">
            // {cat.slugs.length} tiers available
          </div>
          <div className={`grid grid-cols-1 ${cat.slugs.length === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-2 lg:grid-cols-3"} gap-px bg-[#1F2833] border border-[#1F2833]`}>
            {cat.slugs.map((slug) => {
              const svc = servicesContent[slug];
              if (!svc) return null;
              return (
                <Link
                  key={slug}
                  to={`/services/${slug}`}
                  data-testid={`category-tier-${slug}`}
                  className={`relative bg-[#0B0C10] p-6 lg:p-8 flex flex-col te-glow-hover border border-transparent hover:border-[#B91C2C] group ${
                    svc.popular ? "lg:bg-[#0d1117]" : ""
                  }`}
                >
                  {svc.popular && (
                    <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-emerald-400 text-[#0B0C10] px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.22em] font-bold te-emerald-glow-sm">
                      <Star size={10} fill="currentColor" /> Popular
                    </div>
                  )}
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B91C2C]">{svc.kicker}</div>
                  <h3 className="mt-4 font-display text-xl font-bold tracking-tighter leading-tight">{svc.name}</h3>
                  <p className="mt-2 font-mono text-[11px] text-[#C5C6C7] leading-relaxed">{svc.subtitle}</p>

                  <div className="mt-5 border-l-2 border-[#FF3355] pl-3">
                    <div className="font-display text-xl font-bold text-[#FF3355] leading-tight">{svc.priceLabel}</div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C5C6C7]/70 leading-tight">{svc.priceNote}</div>
                  </div>

                  <p className="mt-5 text-sm text-[#F5F5F7] leading-relaxed flex-1">{svc.tagline}</p>

                  <div className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#FF3355] group-hover:text-[#F5F5F7]">
                    View details <ArrowUpRight size={12} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ WHY IT MATTERS ============ */}
      <section className="py-16 lg:py-24 border-b border-[#1F2833]" data-testid="category-why-matters">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4 inline-flex items-center gap-2">
              <TrendingUp size={12} /> // WHY.THIS.MATTERS
            </div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter">
              {sales.whyItMatters.heading}
            </h2>
          </div>
          <div className="space-y-6 text-[#C5C6C7] leading-relaxed">
            <p className="text-base">{sales.whyItMatters.context}</p>
            <div className="border-l-2 border-[#FF3355] pl-5 py-2 bg-[#FF3355]/[0.03]">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355] mb-2 inline-flex items-center gap-1.5">
                <AlertTriangle size={11} /> Cost of doing nothing
              </div>
              <p className="text-sm">{sales.whyItMatters.risk}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COST vs VALUE ============ */}
      <section className="py-16 lg:py-24 border-b border-[#1F2833]" data-testid="category-cost-value">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4 inline-flex items-center gap-2">
            <Target size={12} /> // COST.VS.VALUE
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter max-w-3xl">
            {sales.costValue.heading}
          </h2>

          <div className="mt-12 grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1 bg-[#0d1117] border border-[#B91C2C]/60 p-8 flex flex-col">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">
                investment range
              </div>
              <div className="mt-3 font-display text-3xl lg:text-4xl font-bold text-[#FF3355] leading-tight">
                {sales.costValue.yourInvestment}
              </div>
              {sales.costValue.investmentNote && (
                <div className="mt-3 font-mono text-[11px] text-[#C5C6C7]/80 leading-relaxed">
                  {sales.costValue.investmentNote}
                </div>
              )}
              <div className="mt-6 pt-6 border-t border-[#1F2833]">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]/70 mb-2">
                  comparable market rates
                </div>
                <div className="text-xs text-[#C5C6C7] leading-relaxed line-through decoration-[#FF3355]/60">
                  {sales.costValue.comparable}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 bg-[#0d1117] border border-emerald-400/60 p-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">
                value delivered
              </div>
              <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">
                What every tier includes.
              </h3>
              <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                {sales.costValue.delivered.map((d, i) => (
                  <li
                    key={i}
                    className="te-lime-accent flex items-start gap-2 text-sm text-[#F5F5F7] leading-relaxed"
                  >
                    <Check size={14} className="text-emerald-400 mt-1 shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-[#1F2833] flex items-start gap-3">
                <Zap size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-sm text-[#C5C6C7] font-mono">{sales.costValue.payback}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHY NOW ============ */}
      <section className="py-16 lg:py-24 border-b border-[#1F2833]" data-testid="category-why-now">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4 inline-flex items-center gap-2">
                <Clock size={12} /> // WHY.NOW
              </div>
              <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter max-w-3xl">
                {sales.whyNow.heading}
              </h2>
              <p className="mt-6 text-[#C5C6C7] max-w-3xl leading-relaxed text-base">
                {sales.whyNow.urgency}
              </p>
            </div>
            <div className="lg:min-w-[280px]">
              <div className="bg-[#0d1117] border border-emerald-400/60 p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400 mb-2">
                  fastest tier timeline
                </div>
                <div className="font-display text-2xl font-bold text-[#F5F5F7]">
                  {sales.whyNow.timeline}
                </div>
              </div>
              <Link
                to="/terminal"
                data-testid="category-why-now-cta"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-emerald-400 text-[#0B0C10] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-emerald-300 transition-colors te-emerald-glow-sm"
              >
                <Zap size={13} /> Book scoping call
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 border border-[#1F2833] bg-[#0d1117] p-8 lg:p-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-3">// NEXT.STEP</div>
            <h3 className="font-display text-2xl lg:text-4xl font-bold tracking-tight max-w-2xl">
              Not sure which tier? Talk to a founder.
            </h3>
            <p className="mt-3 text-[#C5C6C7]">30-minute scoping call. No pitch deck.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/services" data-testid="category-back" className="inline-flex items-center gap-2 border border-[#1F2833] hover:border-[#B91C2C] text-[#C5C6C7] hover:text-[#F5F5F7] px-6 py-4 font-mono text-xs uppercase tracking-[0.22em]">
              <ArrowLeft size={14} /> All services
            </Link>
            <Link to="/terminal" data-testid="category-cta" className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#F5F5F7] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors">
              Book Strategy Call <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
